"use client";

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/shadcn-components/ui/drawer";
import { Button } from "@/shadcn-components/ui/button";
import { Separator } from "@/shadcn-components/ui/separator";
import { ChartIcon, TradeIcon, CloseIcon } from "@/components/icons";
import type { VybeOrderInternal } from "@/vybe-trader-client/trade-history/types";
import {
  formatOrderType,
  orderTypeBuySellClass,
} from "@/vybe-trader-client/trade-history/types";
import HistoryDetailsContent from "../HistoryDetailsContent";

interface OrderDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  order: VybeOrderInternal | null;
  onChart: () => void;
  onTrade: () => void;
}

export default function OrderDetailsDrawer({
  isOpen,
  onClose,
  order,
  onChart,
  onTrade,
}: OrderDetailsDrawerProps) {
  if (!order) return null;

  const displayType = formatOrderType(order.type);

  const handleAction = (action: () => void) => {
    action();
    onClose();
  };

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DrawerContent className="max-h-[88vh] flex flex-col">
        <DrawerHeader className="pb-2 px-4 shrink-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <DrawerTitle className="text-xl font-medium text-foreground truncate">
                {order.symbol}
              </DrawerTitle>
              <span
                className={`text-sm shrink-0 ${orderTypeBuySellClass(order.type)}`}
              >
                {displayType}
              </span>
            </div>
            <span className="text-sm text-muted-foreground shrink-0">
              #{order.ticketNumber}
            </span>
          </div>
          <DrawerDescription className="sr-only">Order details</DrawerDescription>
        </DrawerHeader>

        <div className="px-4 overflow-y-auto flex-1 min-h-0 pb-2">
          <HistoryDetailsContent kind="order" data={order} />
        </div>

        <Separator className="shrink-0" />
        <div className="px-4 py-3 grid grid-cols-2 gap-2 shrink-0">
          <Button
            variant="outline"
            onClick={() => handleAction(onChart)}
            className="justify-center gap-2 h-11 text-blue-600 border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"
          >
            <ChartIcon size={18} />
            Chart
          </Button>
          <Button
            variant="outline"
            onClick={() => handleAction(onTrade)}
            className="justify-center gap-2 h-11 text-blue-600 border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"
          >
            <TradeIcon size={18} />
            Trade
          </Button>
        </div>

        <div className="px-4 pb-4 pt-1 shrink-0">
          <DrawerClose asChild>
            <Button variant="secondary" className="w-full h-11 text-base">
              <CloseIcon size={16} className="mr-2" />
              Close
            </Button>
          </DrawerClose>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
